import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment";
import { Divider, Form, Select, Spin } from "antd";
import BackButton from "../../components/button/BackButton";
import MySegmented from "../../components/segmented/Segmented";
import {
  GetInvoiceTemplateApi,
  GetInvoiceTemplateListApi,
} from "../../api/request/invoice";
import { SendInvoiceApi } from "../../api/request/service";
import LoadableButton from "../../components/button/LoadableButton";
import SkeletonTable from "../../components/table/SkeletonTable";
import columns from "../../columns/files/sendInvoice";
import Add from "../../assets/Add";
import { v4 as uuidv4 } from "uuid";

const SendInvoice = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [invoiceForm] = Form.useForm();
  const [segmentedValue, setSegmentedValue] = useState("Standard");
  const [invoiceList, setInvoiceList] = useState([]);
  const [invoiceListLoading, setInvoiceLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceData, setInvoiceData] = useState([]);
  const [isInvoiceTableLoading, setIsInvoiceTableLoading] = useState(false);

  const fileData = state?.fileData;

  const infoList = [
    { label: "User Name", value: fileData?.user?.name || "--" },
    { label: "Email", value: fileData?.user?.email || "--" },
    { label: "Mobile No", value: fileData?.user?.mobile_no || "--" },
    { label: "Date", value: moment().format("ll") },
    { label: "File Number", value: `#${fileData?.file_number || "--"}` },
    { label: "File Name", value: fileData?.service_name_slug || "--", capitalize: true },
  ];

  const subTotal = invoiceData?.reduce((sum, item) => sum + item?.charge, 0);
  const taxAmount = invoiceData?.reduce((sum, item) => sum + ((item?.charge * item?.tax) / 100), 0);
  const total = subTotal + taxAmount;

  const getInvoiceList = async () => {
    try {
      setInvoiceLoading(true);
      const { data } = await GetInvoiceTemplateListApi();
      if (data?.status) {
        setInvoiceList(
          data?.data?.map((item) => {
            return {
              value: item?.id,
              label: item?.name,
            };
          })
        );
      }
    } catch (error) {
    } finally {
      setInvoiceLoading(false);
    }
  };

  const getInvoiceData = async () => {
    setIsInvoiceTableLoading(true);
    try {
      const { data } = await GetInvoiceTemplateApi({
        invoice_template_id: selectedInvoice,
      });
      if (data?.status) {
        const charges = JSON.parse(data?.data?.title || []).map((item) => ({
          ...item,
          id: uuidv4(),
          unit: item?.unit || 1,
          amount: Number(item?.charge || 0),
          tax: Number(item?.tax || 0),
          charge: (item?.unit || 1) * Number(item?.charge || 0),
        }));
        setInvoiceData(charges);
      }
    } catch (error) {
    } finally {
      setIsInvoiceTableLoading(false);
    }
  };

  const handleAddItem = (type = "charge") => {
    setInvoiceData([
      ...invoiceData,
      {
        id: uuidv4(),
        type,
        title: "",
        description: "",
        unit: 1,
        amount: 0,
        tax: 0,
        charge: 0,
      },
    ]);
  };

  const handleAction = (action, record, data) => {
    if (action === "delete") handleDeleteCharge(record);
    else if (action === "edit") handleEditCharge(record, data);
  };

  const handleEditCharge = (record, data) => {
    const updatedInvoice = invoiceData?.map((item) => {
      if (item?.id === record?.id) return { ...item, ...data };
      return item;
    });
    setInvoiceData(updatedInvoice);
  };

  const handleDeleteCharge = (record) => {
    const updatedInvoice = invoiceData?.filter(
      (item) => item?.id !== record?.id
    );
    setInvoiceData(updatedInvoice);
  };

  const sendInvoice = async () => {
    setIsInvoiceTableLoading(true);
    try {
      const { data } = await SendInvoiceApi({
        file_id: id,
        invoice_temp: invoiceData,
        sub_total: subTotal,
        tax_rate_amount: taxAmount,
        total,
      });

      if (data?.status) {
        toast.success(data?.message);
        navigate(`/file/${state?.fileTypeId}/${id}`);
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsInvoiceTableLoading(false);
    }
  };

  useEffect(() => {
    if (id) getInvoiceList();
  }, [id]);

  useEffect(() => {
    if (selectedInvoice) getInvoiceData();
  }, [selectedInvoice]);

  useEffect(() => {
    invoiceForm.resetFields();
    setSelectedInvoice(null);
    setInvoiceData([]);
  }, [segmentedValue]);

  return (
    <div className="bg-white border rounded-xl p-5">
      {invoiceListLoading ? (
        <div className="flex justify-center flex-col">
          <Spin />
        </div>
      ) : (
        <>
          <div className="flex md:flex-row flex-col justify-between gap-5 mb-5">
            <div className="flex items-center gap-3">
              <BackButton />
              <h2 className="title">Send Invoice</h2>
            </div>
            <MySegmented
              options={["Standard", "Customize"]}
              value={segmentedValue}
              setValue={setSegmentedValue}
            />
          </div>

          <div className="border rounded-xl p-5">
            <div className="grid grid-cols-2 gap-5">
              {infoList?.map(({ label, value, capitalize }, index) => (
                <div key={index} className="flex items-center gap-3 capitalize">
                  <p className="font-bold">{label}:</p>
                  <p className={`${capitalize && "capitalize"}`}>{value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 space-y-5">
            <Form
              autoComplete="off"
              layout="vertical"
              form={invoiceForm}
              initialValues={{ invoice_temp_id: null }}
              onFinish={sendInvoice}
            >
              {segmentedValue === "Standard" && (
                <Form.Item
                  name="invoice_temp_id"
                  label="Select Standard Invoice Template"
                  className="mb-0"
                  rules={[
                    { required: true, message: "Please select invoice!" },
                  ]}
                >
                  <Select
                    placeholder="Select Invoice"
                    options={invoiceList}
                    size="large"
                    onChange={(value) => setSelectedInvoice(value)}
                    className="!w-[250px]"
                  />
                </Form.Item>
              )}
            </Form>

            {(segmentedValue === "Standard" && selectedInvoice) ||
              segmentedValue === "Customize" ? (
              <>
                <SkeletonTable
                  columns={columns(handleAction)}
                  data={invoiceData}
                  isLoading={isInvoiceTableLoading}
                />

                <div className="flex gap-5 mt-5">
                  <button
                    type="button"
                    className="btn-primary !px-4 !text-sm flex items-center gap-2"
                    onClick={() => handleAddItem("charge")}
                  >
                    <Add />
                    <span>Add Charge</span>
                  </button>
                  <button
                    type="button"
                    className="btn-primary !px-4 !text-sm flex items-center gap-2"
                    onClick={() => handleAddItem("deduction")}
                  >
                    <Add />
                    <span>Add Deduction</span>
                  </button>
                </div>

                {invoiceData?.length ? (
                  <div className="flex flex-col items-end justify-end mt-5">
                    <div className="w-[230px] flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-5 ps-5">
                        <p className="font-medium">Sub Total</p>
                        <p>${subTotal}</p>
                      </div>
                      <div className="flex items-center justify-between gap-5 ps-5">
                        <p className="font-medium">Tax Amount</p>
                        <p>${taxAmount}</p>
                      </div>
                      <Divider className="m-0 bg-black" />
                      <div className="flex items-center justify-between gap-5 ps-5 text-lg font-semibold">
                        <p>Total</p>
                        <p>${total}</p>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="mt-5 flex gap-5">
                  <LoadableButton
                    lable="Submit"
                    onClick={() => invoiceForm.submit()}
                    disabled={isInvoiceTableLoading}
                  />
                </div>
              </>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
};

export default SendInvoice;
